import {
  validateEnumValue,
  validateNotNullishObject,
  validateOfExpectedValue,
  validatePrimitive,
} from "@faro-lotv/foundation";
import { WorkspaceIntegrationProviderId } from "@faro-lotv/ielement-types";
import { AuthorizationMessage } from "./provider-authorization-types";

interface Props {
  /** Base URL of the Core API */
  baseUrl: string;
  /** ID of the provider */
  providerId: WorkspaceIntegrationProviderId;
}

/**
 * Service to handle the authorization flow for a specific provider.
 */
export class ProviderAuthorizationService {
  #baseUrl: string;
  #providerId: WorkspaceIntegrationProviderId;
  #authorizationWindow: Window | null = null;
  #onAuthorizationMessage: (messageEvent: MessageEvent) => void = () =>
    undefined;

  /**
   * @param baseUrl - The base URL of the Core API
   * @param providerId - The ID of the provider
   */
  constructor({ baseUrl, providerId }: Props) {
    this.#baseUrl = baseUrl;
    this.#providerId = providerId;
  }

  /**
   * Starts the provider authorization flow.
   *
   * @returns A promise that resolves to true if the authorization was successful, false if canceled
   * @throws {Error} if the authorization fails
   */
  public async authorizeProvider(): Promise<boolean> {
    this.openAuthorizationWindow();
    return await this.waitForAuthorizationMessage();
  }

  /**
   * Closes the authorization window and removes the window event listener for the authorization message
   */
  public closeAuthorizationWindow(): void {
    try {
      window.removeEventListener("message", this.#onAuthorizationMessage);
      this.#authorizationWindow?.close();
      this.#authorizationWindow = null;
    } catch {
      // Ignore any errors from closing the authorization window as it's not a critical operation
    }
  }

  /**
   * Opens the authorization window.
   *
   * @throws {Error} if it fails to construct the authorization URL or open the window
   */
  private openAuthorizationWindow(): void {
    // Construct authorization URL
    const url = new URL("/v1/users/tokens/connect", this.#baseUrl);
    url.searchParams.set("providerId", this.#providerId);

    // Try to reuse existing window if still open
    const existingWindow = this.#authorizationWindow;
    if (existingWindow && !existingWindow.closed) {
      existingWindow.focus();
      existingWindow.location.href = url.href;
      return;
    }

    // Open new authorization window
    const windowName = `provider-auth-${this.#providerId}`;
    this.#authorizationWindow = window.open(
      url.href,
      windowName,
      "width=600,height=700",
    );

    // Check if window opened successfully
    if (!this.#authorizationWindow || this.#authorizationWindow.closed) {
      throw new Error(
        "Failed to open authorization window (it may have been blocked by the browser).",
      );
    }

    this.#authorizationWindow.focus();
  }

  /**
   * Waits for the authorization message from the authorization window.
   *
   * @returns A promise that resolves to true if the authorization was successful, false if canceled
   * @throws {Error} if the authorization fails or times out
   */
  private async waitForAuthorizationMessage(): Promise<boolean> {
    return await new Promise<boolean>((resolve, reject) => {
      this.#onAuthorizationMessage = (messageEvent: MessageEvent): void => {
        try {
          // Validate message origin and source
          if (
            !messageEvent.source ||
            !this.#authorizationWindow ||
            messageEvent.source !== this.#authorizationWindow ||
            !this.isTrustedOrigin(messageEvent.origin)
          ) {
            return;
          }

          // Handle canceled authorization message
          if (this.isCanceledAuthorizationMessage(messageEvent.data)) {
            resolve(false);
          }

          // Parse message and validate format
          const parsedData = this.parseAuthorizationData(messageEvent.data);
          if (!this.isAuthorizationMessage(parsedData)) {
            throw new Error("Received message with unexpected data format.");
          }

          // Validate that message is successful
          if (
            parsedData.status === "error" ||
            parsedData.connectStatus === false
          ) {
            throw new Error(parsedData.message || "Authorization failed.");
          }

          resolve(true);
        } catch (error) {
          reject(error);
        }
      };

      window.addEventListener("message", this.#onAuthorizationMessage);
    });
  }

  /**
   * Parses the raw authorization data from the message event.
   *
   * @param rawData The raw data to parse
   * @returns Parsed authorization data
   * @throws {Error} if the raw data is not a string or if parsing fails
   */
  private parseAuthorizationData(rawData: unknown): unknown {
    if (typeof rawData !== "string") {
      throw new Error("Invalid authorization message format: expected string.");
    }

    try {
      return JSON.parse(rawData);
    } catch {
      throw new Error(`Failed to parse authorization message JSON: ${rawData}`);
    }
  }

  /**
   * @returns Whether the passed origin is trusted (matches the CoreAPI base URL)
   * @param origin Origin to validate
   */
  private isTrustedOrigin(origin: string): boolean {
    try {
      const expected = new URL(this.#baseUrl);
      const received = new URL(origin);
      return expected.hostname === received.hostname;
    } catch {
      return false;
    }
  }

  /**
   * @returns Whether the passed data is a canceled authorization message sent from CoreAPI
   * @param data Data to validate
   */
  private isCanceledAuthorizationMessage(data: unknown): boolean {
    return typeof data === "string" && data === "OAUTH_ERROR_USER_CANCELED";
  }

  /**
   * @returns Whether the passed data is of type AuthorizationMessage
   * @param data Data to validate
   */
  private isAuthorizationMessage(data: unknown): data is AuthorizationMessage {
    return (
      validateNotNullishObject<AuthorizationMessage>(
        data,
        "AuthorizationMessage",
      ) &&
      validatePrimitive(data, "connectStatus", "boolean") &&
      validatePrimitive(data, "message", "string") &&
      validateEnumValue(data.providerId, WorkspaceIntegrationProviderId) &&
      (validateOfExpectedValue(data, "status", "success") ||
        validateOfExpectedValue(data, "status", "error"))
    );
  }
}
